import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { deleteEvent, getEventPage } from '../../features/event/eventSlice';
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useTitle } from '../../hooks/titleProvider';
import EventsTable from '../ui/table/eventTable';
import EventPagination from './eventPagination';
import AddEditEventDialog from './addEditEventDialog';
import PrimaryButton from '../ui/buttons/primaryButton';
import { useAuth } from '../../contexts/authContext';

const PAGE_SIZE = 10;
const EVENT_SCOPE = 'past';

const EventArchive = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const eventPage = useSelector((state) => state.event.eventPage);
  const eventPageScope = useSelector((state) => state.event.eventPageScope);
  const eventPageLoading = useSelector((state) => state.event.eventPageLoading);
  const [page, setPage] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const { currentWorkspace } = useAuth();

  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const { setTitle } = useTitle();
  setTitle('Events');

  const pageReady = eventPageScope === EVENT_SCOPE && !eventPageLoading;

  useEffect(() => {
    dispatch(getEventPage({ scope: EVENT_SCOPE, limit: PAGE_SIZE, offset: page * PAGE_SIZE }));
  }, [dispatch, page, currentWorkspace?.id, refreshToken]);

  // A different workspace has a different list, so start it at page one.
  useEffect(() => {
    setPage(0);
  }, [currentWorkspace?.id]);

  // An edit can move the last event on this page out of the list; step back
  // instead of sitting on an empty page.
  useEffect(() => {
    if (pageReady && page > 0 && eventPage.events.length === 0) {
      setPage((currentPage) => Math.max(0, currentPage - 1));
    }
  }, [pageReady, page, eventPage.events.length]);

  const refreshEvents = () => {
    setRefreshToken((token) => token + 1);
  };

  const handleEditOpen = async (event) => {
    setSelectedEvent(event);
    setOpenEdit(true);
  };

  const handleEditClose = async () => {
    setSelectedEvent(null);
    setOpenEdit(false);
    refreshEvents();
  };

  const handleDeleteClose = async () => {
    setOpenDelete(false);
  };

  const handleDeleteOpen = async (event) => {
    setSelectedEvent(event);
    setOpenDelete(true);
  };

  const handleConfirm = async () => {
    const removingOnlyRow = eventPage.events.length === 1 && page > 0;
    if (selectedEvent) {
      await dispatch(deleteEvent(selectedEvent.id));
    }

    setOpenDelete(false);
    if (removingOnlyRow) {
      setPage((currentPage) => currentPage - 1);
    } else {
      refreshEvents();
    }
  };

  return (
    <>
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        mb={2}>
        <Typography variant="h5" component="h1">
          Past Events
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/events')}>
          Back to events
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column'
        }}>
        {!pageReady ? (
          <Box
            display="flex"
            flexDirection={'row'}
            justifyContent={'center'}
            alignItems={'center'}
            m={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <EventsTable
              events={eventPage.events}
              onEdit={(event) => handleEditOpen(event)}
              onDelete={(event) => handleDeleteOpen(event)}
            />
            <EventPagination
              count={eventPage.total}
              page={page}
              rowsPerPage={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </Box>

      {selectedEvent ? (
        <AddEditEventDialog
          open={openEdit}
          onClose={() => handleEditClose()}
          event={selectedEvent}
        />
      ) : (
        <AddEditEventDialog open={openEdit} onClose={() => handleEditClose()} />
      )}

      <Dialog
        open={openDelete}
        onClose={handleDeleteClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{selectedEvent ? selectedEvent.name : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this event? It will be hidden from your events and can
            be brought back by the assistant.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PrimaryButton variant="contained" onClick={handleDeleteClose}>
            Cancel
          </PrimaryButton>
          <PrimaryButton onClick={handleConfirm} autoFocus>
            Confirm
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventArchive;
